import { useFields, useFieldsInPage } from '@modusign/react-signing';
import { FieldNode } from '@modusign/react-signing/types';
import React, { useMemo } from 'react';

import {
  isRenderableSignerFieldNode,
  isRequesterFieldNode,
} from '@app/features/apply-react-signing/components/FieldRenderer/fieldTypes';
import {
  FieldComponent,
  RequesterFieldComponentByFieldNodeType,
  SignerFieldComponentByFieldNodeType,
} from '@app/features/apply-react-signing/components/FieldRenderer/Setup/FieldRenderers/fields';
import { ReactSigningRendererProps } from '@app/features/apply-react-signing/components/FieldRenderer/types';
import { OptimalFieldsProvider } from '@app/features/apply-react-signing/libs/hooks/useCollectionOptimal';

interface ReactSigningConfigurationFieldRendererProps extends ReactSigningRendererProps {
  visibilityOption?: 'SIGNER_FIELDS_ONLY' | 'REQUESTER_FIELDS_ONLY' | 'ALL_FIELDS';
}

export const ReactSigningConfigurationFieldRenderer = React.memo(
  (props: ReactSigningConfigurationFieldRendererProps) => {
    const { page, visibilityOption = 'ALL_FIELDS' } = props;

    const { fields: pageFields } = useFieldsInPage(page);
    const { fields, removeField, changeFields } = useFields();

    const filteredFields = useMemo(
      () =>
        pageFields.filter((field) => {
          switch (visibilityOption) {
            case 'SIGNER_FIELDS_ONLY':
              return isRenderableSignerFieldNode(field);
            case 'REQUESTER_FIELDS_ONLY':
              return isRequesterFieldNode(field);
            case 'ALL_FIELDS':
            default:
              return true;
          }
        }),
      [pageFields, visibilityOption]
    );

    const dataLabelDuplicationRecords: Record<string, number> = {};
    const fieldRecord: Record<string, FieldNode> = {};

    fields.forEach((field) => {
      if (!dataLabelDuplicationRecords[field.dataLabel]) {
        dataLabelDuplicationRecords[field.dataLabel] = 0;
      }
      dataLabelDuplicationRecords[field.dataLabel] += 1;
      fieldRecord[field.id] = field;

      if (field.type === 'AUTOFILL_GROUP') {
        field.children.forEach((child) => {
          if (!dataLabelDuplicationRecords[child.dataLabel]) {
            dataLabelDuplicationRecords[child.dataLabel] = 0;
          }
          dataLabelDuplicationRecords[child.dataLabel] += 1;
          fieldRecord[child.id] = child;
        });
      }
    });

    return (
      <>
        {filteredFields.map((field) => {
          let FieldComponent: FieldComponent | null = null;
          if (isRenderableSignerFieldNode(field)) {
            FieldComponent = SignerFieldComponentByFieldNodeType[field.type];
          } else if (isRequesterFieldNode(field)) {
            FieldComponent = RequesterFieldComponentByFieldNodeType[field.type];
          }
          if (FieldComponent) {
            return (
              <OptimalFieldsProvider
                changeFields={changeFields}
                dataLabelDuplicationRecords={dataLabelDuplicationRecords}
                fieldRecord={fieldRecord}
                fields={fields}
                id={field.id}
                key={field.id}
                removeField={removeField}
              >
                <FieldComponent key={field.id} {...props} fieldId={field.id} />
              </OptimalFieldsProvider>
            );
          }
          return null;
        })}
      </>
    );
  }
);
